import { buildArticlePath } from './articlePage';
import { sanityClient } from './client';
import { buildTeamRevealPath } from './teamReveal';

/**
 * Kill switch for the WordPress-path redirects. With it off the catch-all
 * serves every root path from WordPress and never reads Sanity.
 */
export const IS_LEGACY_SLUG_REDIRECT_ENABLED = true;

const MINUTE_MS = 60 * 1000;
/**
 * How long one lambda keeps the map. The publish webhook (FFH-2627) regenerates
 * a migrated article's old URL straight away, and the lambda doing that must
 * not answer from a map older than the publish, so the window is short; a
 * root-path rebuild still costs at most one CDN-cached read per lambda per
 * window (FFH-2868).
 */
export const LEGACY_SLUG_MAP_TTL_MS = MINUTE_MS;

/**
 * Every migrated document in one read. The author is joined here so a reveal
 * resolves to its nested path without a second query.
 */
export const legacySlugMapQuery = `
  *[_type in ["articlePage", "teamReveal"] && defined(legacySlug.current) && defined(slug.current)] {
    _type,
    "legacySlug": legacySlug.current,
    "slug": slug.current,
    "authorSlug": author->slug.current
  }
`;

export type LegacySlugEntry = {
  _type: 'articlePage' | 'teamReveal';
  legacySlug: string;
  slug: string;
  authorSlug?: string | null;
};

type CachedMap = {
  paths: Map<string, string>;
  fetchedAt: number;
};

let cached: CachedMap | undefined;
let inFlight: Promise<Map<string, string> | undefined> | undefined;

/** WordPress paths arrive with or without slashes and in any case. */
export const normaliseLegacySlug = (value: string): string =>
  value.trim().replace(/^\/+|\/+$/g, '').toLowerCase();

const pathForEntry = (entry: LegacySlugEntry): string | undefined => {
  if (entry._type === 'teamReveal') {
    return entry.authorSlug
      ? buildTeamRevealPath(entry.authorSlug, entry.slug)
      : undefined;
  }
  return buildArticlePath(entry.slug);
};

export const buildLegacyPathMap = (
  entries: LegacySlugEntry[]
): Map<string, string> => {
  const paths = new Map<string, string>();
  for (const entry of entries) {
    const path = pathForEntry(entry);
    if (path) paths.set(normaliseLegacySlug(entry.legacySlug), path);
  }
  return paths;
};

const fetchMap = async (
  now: number
): Promise<Map<string, string> | undefined> => {
  try {
    const entries = await sanityClient.fetch<LegacySlugEntry[]>(
      legacySlugMapQuery
    );
    cached = { paths: buildLegacyPathMap(entries ?? []), fetchedAt: now };
    return cached.paths;
  } catch (error) {
    // A stale map beats no map: the redirects it holds are still right.
    console.error('[legacy-slug-map] refresh failed', error);
    return cached?.paths;
  } finally {
    inFlight = undefined;
  }
};

const loadMap = (now: number): Promise<Map<string, string> | undefined> => {
  if (cached && now - cached.fetchedAt < LEGACY_SLUG_MAP_TTL_MS) {
    return Promise.resolve(cached.paths);
  }
  // Concurrent rebuilds on a cold lambda share one read.
  inFlight ??= fetchMap(now);
  return inFlight;
};

type LookupOptions = {
  isEnabled?: boolean;
  now?: number;
};

/**
 * The new path for a WordPress root slug, or null when nothing has claimed
 * it and WordPress should render as before. Never throws.
 */
export const getLegacyRedirectPath = async (
  legacySlug: string,
  { isEnabled = IS_LEGACY_SLUG_REDIRECT_ENABLED, now = Date.now() }: LookupOptions = {}
): Promise<string | null> => {
  if (!isEnabled) return null;
  const paths = await loadMap(now);
  return paths?.get(normaliseLegacySlug(legacySlug)) ?? null;
};

/**
 * Drops the WordPress slugs that a Sanity document has claimed. The catch-all
 * uses it to keep migrated posts out of the prerender list: Next refuses a
 * redirect from getStaticProps while prerendering, so a migrated post must be
 * built on request, where the redirect is allowed. With no map the list is
 * returned unchanged and WordPress renders as before.
 */
export const withoutMigratedSlugs = async (
  slugs: string[],
  { isEnabled = IS_LEGACY_SLUG_REDIRECT_ENABLED, now = Date.now() }: LookupOptions = {}
): Promise<string[]> => {
  if (!isEnabled) return slugs;
  const paths = await loadMap(now);
  if (!paths) return slugs;
  return slugs.filter((slug) => !paths.has(normaliseLegacySlug(slug)));
};

/** Test hook: forget the cached map. */
export const resetLegacySlugMapCache = (): void => {
  cached = undefined;
  inFlight = undefined;
};
